import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  api,
  ApiError,
  type ReportKind,
  type SpapiFetchKind,
  type SpapiFetchRun,
  type SpapiKindStatus,
  type SpapiStatus,
  type SpapiTestResult,
} from '../api/client.js';
import { Alert, Button, Card, Field, Input, Select, Spinner, TableScroll } from './ui.js';
import { dateTime, day, integer } from '../format.js';

/** A fetch kind's label, including the poll/fetch kinds that are not report kinds. */
function fetchKindLabel(kind: SpapiFetchKind, kindLabels: Record<ReportKind, string>): string {
  if (kind === 'offers') return 'Competitor offers';
  if (kind === 'catalog') return 'Product catalog';
  if (kind === 'inbound') return 'Inbound shipments';
  return kindLabels[kind];
}

/**
 * The Amazon connection card on the Imports page.
 *
 * When the Selling Partner API is not configured it explains what to do and
 * links to the setup guide. When it is, it shows the marketplace, the nightly
 * schedule, a "Test connection" button (a token exchange, ok/failed only), a
 * per-kind "Fetch now", a backfill form for the windowed kinds, and the recent
 * runs. It never shows a credential — the API does not send one.
 */

const SETUP_GUIDE_URL =
  'https://github.com/nickszkodyn/amazon-seller-app/blob/main/docs/sp-api-setup.md';

/** The windowed kinds a backfill can walk. */
const BACKFILLABLE: ReportKind[] = ['all_orders', 'ledger_detail', 'sales_traffic_json'];

const STATUS_TONE: Record<SpapiFetchRun['status'], string> = {
  done: 'text-ok-text',
  failed: 'text-danger-text',
  skipped: 'text-muted',
  running: 'text-body',
  queued: 'text-muted',
};

export function SpapiConnectionCard({ kindLabels }: { kindLabels: Record<ReportKind, string> }) {
  const queryClient = useQueryClient();
  const [testResult, setTestResult] = useState<SpapiTestResult | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [backfillNotice, setBackfillNotice] = useState<string | null>(null);
  const [backfill, setBackfill] = useState<{ kind: ReportKind; from: string; to: string }>({
    kind: 'all_orders',
    from: '',
    to: '',
  });

  const status = useQuery({
    queryKey: ['spapi-status'],
    queryFn: () => api.get<SpapiStatus>('/api/spapi/status'),
    // Poll while a fetch, the offers poll, or the catalog fetch is in flight so
    // rows and runs update.
    refetchInterval: (query) =>
      (query.state.data?.running.length ?? 0) > 0 ||
      query.state.data?.offers.polling ||
      query.state.data?.catalog.fetching ||
      query.state.data?.inbound.fetching
        ? 4000
        : false,
  });
  const runs = useQuery({
    queryKey: ['spapi-runs'],
    queryFn: () => api.get<{ runs: SpapiFetchRun[] }>('/api/spapi/runs'),
    refetchInterval: (query) =>
      (status.data?.running.length ?? 0) > 0 ||
      status.data?.offers.polling ||
      status.data?.catalog.fetching ||
      status.data?.inbound.fetching ||
      (query.state.data?.runs.length ?? 0) === 0
        ? 4000
        : false,
    enabled: status.data?.configured === true,
  });

  async function refresh() {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: ['spapi-status'] }),
      queryClient.invalidateQueries({ queryKey: ['spapi-runs'] }),
    ]);
  }

  const test = useMutation({
    mutationFn: () => api.post<SpapiTestResult>('/api/spapi/test-connection'),
    onSuccess: async (result) => {
      setActionError(null);
      setTestResult(result);
      await refresh();
    },
    onError: (caught: unknown) =>
      setActionError(caught instanceof ApiError ? caught.message : 'The test failed.'),
  });

  const fetchNow = useMutation({
    mutationFn: (kind: ReportKind) => api.post('/api/spapi/fetch', { kind }),
    onSuccess: async () => {
      setActionError(null);
      await refresh();
    },
    onError: (caught: unknown) =>
      setActionError(caught instanceof ApiError ? caught.message : 'The fetch could not start.'),
  });

  const runBackfill = useMutation({
    mutationFn: (body: { kind: ReportKind; from: string; to: string }) =>
      api.post<{ notice?: string }>('/api/spapi/backfill', body),
    onSuccess: async (result) => {
      setActionError(null);
      // The server narrows a range Amazon can no longer return (the ledger's
      // 18-month retention) and says so.
      setBackfillNotice(result?.notice ?? null);
      await refresh();
    },
    onError: (caught: unknown) => {
      setBackfillNotice(null);
      setActionError(caught instanceof ApiError ? caught.message : 'The backfill could not start.');
    },
  });

  const resumeBackfill = useMutation({
    mutationFn: (kind: ReportKind) => api.post('/api/spapi/backfill/resume', { kind }),
    onSuccess: async () => {
      setActionError(null);
      await refresh();
    },
    onError: (caught: unknown) =>
      setActionError(
        caught instanceof ApiError ? caught.message : 'The backfill could not resume.',
      ),
  });

  const pollOffers = useMutation({
    mutationFn: () => api.post('/api/spapi/offers/poll'),
    onSuccess: async () => {
      setActionError(null);
      await refresh();
    },
    onError: (caught: unknown) =>
      setActionError(
        caught instanceof ApiError ? caught.message : 'The offers poll could not start.',
      ),
  });

  const fetchCatalog = useMutation({
    mutationFn: () => api.post('/api/spapi/catalog/fetch'),
    onSuccess: async () => {
      setActionError(null);
      await refresh();
    },
    onError: (caught: unknown) =>
      setActionError(
        caught instanceof ApiError ? caught.message : 'The catalog fetch could not start.',
      ),
  });

  const fetchInbound = useMutation({
    mutationFn: () => api.post('/api/spapi/inbound/fetch'),
    onSuccess: async () => {
      setActionError(null);
      await refresh();
    },
    onError: (caught: unknown) =>
      setActionError(
        caught instanceof ApiError ? caught.message : 'The inbound fetch could not start.',
      ),
  });

  // The POST only accepts the fetch (202); nothing is stored yet. Invalidate the
  // shared image-asins set when the catalog run FINISHES — the card already polls
  // status, so `catalog.fetching` flipping from true to false is the "done" signal —
  // so thumbnails across the app pick up newly cached images without a stale set.
  const catalogFetching = status.data?.catalog.fetching ?? false;
  const wasCatalogFetching = useRef(false);
  useEffect(() => {
    if (wasCatalogFetching.current && !catalogFetching) {
      void queryClient.invalidateQueries({ queryKey: ['catalog', 'image-asins'] });
    }
    wasCatalogFetching.current = catalogFetching;
  }, [catalogFetching, queryClient]);

  if (status.isPending) return <Spinner label="Loading the Amazon connection" />;
  if (status.isError) return <Alert>{status.error.message}</Alert>;
  const data = status.data;

  return (
    <Card className="p-5">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-medium text-body">Amazon connection</h2>
        {data.configured && (
          <span className="text-xs text-muted">
            {data.marketplaceId}
            {data.marketplaceCountry ? ` · ${data.marketplaceCountry}` : ''}
          </span>
        )}
      </div>

      {!data.configured ? (
        <div className="mt-3 space-y-2">
          <Alert tone="info">
            The Selling Partner API is not configured, so reports are fetched by hand and uploaded
            above. Set <code>SPAPI_CLIENT_ID</code>, <code>SPAPI_CLIENT_SECRET</code>, and{' '}
            <code>SPAPI_REFRESH_TOKEN</code> on the server to let the app fetch them on its own.
          </Alert>
          <p className="text-sm text-muted">
            <a
              className="text-brand-600 underline"
              href={SETUP_GUIDE_URL}
              target="_blank"
              rel="noreferrer"
            >
              Selling Partner API setup guide
            </a>{' '}
            (docs/sp-api-setup.md) walks through registering the app and getting these values.
          </p>
        </div>
      ) : (
        <div className="mt-3 space-y-4">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-muted">
            <span>
              Nightly at <span className="text-body">{data.schedule}</span>
              {data.nextRunAt ? ` — next ${dateTime(data.nextRunAt)}` : ''}
            </span>
            {data.lastTokenExchange && (
              <span>
                Last token exchange:{' '}
                <span className={data.lastTokenExchange.ok ? 'text-ok-text' : 'text-danger-text'}>
                  {data.lastTokenExchange.ok ? 'ok' : 'failed'}
                </span>{' '}
                {dateTime(data.lastTokenExchange.at)}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            <Button variant="secondary" disabled={test.isPending} onClick={() => test.mutate()}>
              {test.isPending ? 'Testing…' : 'Test connection'}
            </Button>
            {testResult && (
              <span className={`text-sm ${testResult.ok ? 'text-ok-text' : 'text-danger-text'}`}>
                {testResult.ok
                  ? `Connected (${dateTime(testResult.at)}).`
                  : `Failed: ${testResult.message ?? 'the token exchange did not succeed.'}`}
              </span>
            )}
          </div>

          {actionError && <Alert>{actionError}</Alert>}

          <KindTable
            kinds={data.kinds}
            running={data.running}
            kindLabels={kindLabels}
            onFetch={(kind) => fetchNow.mutate(kind)}
            busy={fetchNow.isPending}
          />

          {data.resumableBackfills.length > 0 && (
            <ResumeBackfills
              resumable={data.resumableBackfills}
              kindLabels={kindLabels}
              running={data.running}
              onResume={(kind) => resumeBackfill.mutate(kind)}
              busy={resumeBackfill.isPending}
            />
          )}

          <BackfillForm
            value={backfill}
            kindLabels={kindLabels}
            onChange={setBackfill}
            onSubmit={() => runBackfill.mutate(backfill)}
            busy={runBackfill.isPending}
          />
          {backfillNotice && (
            <p className="text-xs text-muted" role="status">
              {backfillNotice}
            </p>
          )}

          <OffersSection
            offers={data.offers}
            onPoll={() => pollOffers.mutate()}
            busy={pollOffers.isPending}
          />

          <CatalogSection
            catalog={data.catalog}
            onFetch={() => fetchCatalog.mutate()}
            busy={fetchCatalog.isPending}
          />

          <InboundSection
            inbound={data.inbound}
            onFetch={() => fetchInbound.mutate()}
            busy={fetchInbound.isPending}
          />

          <RunsTable runs={runs.data?.runs ?? []} kindLabels={kindLabels} />
        </div>
      )}
    </Card>
  );
}

function KindTable({
  kinds,
  running,
  kindLabels,
  onFetch,
  busy,
}: {
  kinds: SpapiKindStatus[];
  running: ReportKind[];
  kindLabels: Record<ReportKind, string>;
  onFetch: (kind: ReportKind) => void;
  busy: boolean;
}) {
  const runningSet = new Set(running);
  return (
    <TableScroll>
      <table className="w-full text-sm" aria-label="SP-API report kinds">
        <thead className="border-b border-line text-left text-xs text-muted">
          <tr>
            <th className="px-3 py-2 font-medium">Report</th>
            <th className="px-3 py-2 font-medium">Last fetch</th>
            <th className="px-3 py-2 font-medium"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-line">
          {kinds.map((k) => (
            <tr key={k.kind}>
              <td className="px-3 py-2">
                <div className="text-body">{kindLabels[k.kind]}</div>
                <div className="text-xs text-muted">{k.reportType}</div>
              </td>
              <td className="px-3 py-2">
                <LastFetch fetch={k.lastFetch} />
              </td>
              <td className="px-3 py-2 text-right">
                <Button
                  variant="ghost"
                  disabled={busy || runningSet.has(k.kind)}
                  onClick={() => onFetch(k.kind)}
                >
                  {runningSet.has(k.kind) ? 'Fetching…' : 'Fetch now'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </TableScroll>
  );
}

function LastFetch({ fetch }: { fetch: SpapiFetchRun | null }) {
  if (!fetch) return <span className="text-muted">Never</span>;
  return (
    <div>
      <span className={STATUS_TONE[fetch.status]}>{fetch.status}</span>{' '}
      <span className="text-muted">{dateTime(fetch.createdAt)}</span>
      {fetch.status === 'done' && (
        <div className="text-xs text-muted">
          {integer(fetch.rowCount)} rows: {integer(fetch.inserted)} new, {integer(fetch.updated)}{' '}
          updated, {integer(fetch.skipped)} already present
        </div>
      )}
      {fetch.error && (
        <div
          className={`max-w-md text-xs break-words ${
            fetch.status === 'failed' ? 'text-danger-text' : 'text-muted'
          }`}
        >
          {fetch.error}
        </div>
      )}
    </div>
  );
}

function BackfillForm({
  value,
  kindLabels,
  onChange,
  onSubmit,
  busy,
}: {
  value: { kind: ReportKind; from: string; to: string };
  kindLabels: Record<ReportKind, string>;
  onChange: (next: { kind: ReportKind; from: string; to: string }) => void;
  onSubmit: () => void;
  busy: boolean;
}) {
  const canSubmit = value.from !== '' && value.to !== '' && value.from <= value.to && !busy;
  return (
    <div className="rounded-md bg-sunken p-3">
      <h3 className="text-sm font-medium text-body">Backfill history</h3>
      <p className="mt-1 text-xs text-muted">
        Walk a date range in 30-day chunks (sales and traffic one day per chunk). Re-fetching a day
        is safe; the plan is saved, so an interrupted backfill can be resumed.
      </p>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-4">
        <Field label="Report">
          <Select
            value={value.kind}
            onChange={(e) => onChange({ ...value, kind: e.target.value as ReportKind })}
          >
            {BACKFILLABLE.map((k) => (
              <option key={k} value={k}>
                {kindLabels[k]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="From">
          <Input
            type="date"
            value={value.from}
            onChange={(e) => onChange({ ...value, from: e.target.value })}
          />
        </Field>
        <Field label="To">
          <Input
            type="date"
            value={value.to}
            onChange={(e) => onChange({ ...value, to: e.target.value })}
          />
        </Field>
        <div className="flex items-end">
          <Button onClick={onSubmit} disabled={!canSubmit}>
            {busy ? 'Starting…' : 'Backfill'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function ResumeBackfills({
  resumable,
  kindLabels,
  running,
  onResume,
  busy,
}: {
  resumable: { kind: ReportKind; queued: number }[];
  kindLabels: Record<ReportKind, string>;
  running: ReportKind[];
  onResume: (kind: ReportKind) => void;
  busy: boolean;
}) {
  const runningSet = new Set(running);
  return (
    <Alert tone="warning">
      <div className="space-y-2">
        <p className="text-sm">
          A backfill was interrupted. Its remaining windows are saved and can be resumed.
        </p>
        <div className="flex flex-wrap gap-2">
          {resumable.map((r) => (
            <Button
              key={r.kind}
              variant="secondary"
              disabled={busy || runningSet.has(r.kind)}
              onClick={() => onResume(r.kind)}
            >
              {runningSet.has(r.kind)
                ? `Resuming ${kindLabels[r.kind]}…`
                : `Resume ${kindLabels[r.kind]} (${integer(r.queued)} left)`}
            </Button>
          ))}
        </div>
      </div>
    </Alert>
  );
}

/**
 * The competitor-offers poll: a "Poll offers now" button, the last poll's outcome,
 * and — when Amazon last refused for the Pricing role — a notice pointing at the
 * setup guide. The nightly cycle polls offers after the reports; this is the manual
 * trigger.
 */
function OffersSection({
  offers,
  onPoll,
  busy,
}: {
  offers: SpapiStatus['offers'];
  onPoll: () => void;
  busy: boolean;
}) {
  const { lastPoll, polling, pricingRoleMissing } = offers;
  return (
    <div className="rounded-md bg-sunken p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-body">Competitor offers</h3>
          <p className="mt-1 text-xs text-muted">
            Who is competing on each Active ASIN (and the current booking&rsquo;s matched lines):
            seller count, lowest FBA price, and the Buy Box. Polled nightly after the reports.
          </p>
        </div>
        <Button variant="secondary" disabled={busy || polling} onClick={onPoll}>
          {polling ? 'Polling…' : 'Poll offers now'}
        </Button>
      </div>

      {pricingRoleMissing && (
        <div className="mt-3">
          <Alert tone="warning">
            Pricing role not granted. Amazon refused the Product Pricing API (403). Request the
            Pricing role for the app —{' '}
            <a
              className="text-brand-600 underline"
              href={SETUP_GUIDE_URL}
              target="_blank"
              rel="noreferrer"
            >
              setup guide
            </a>{' '}
            (docs/sp-api-setup.md). The nightly cycle re-checks once a day.
          </Alert>
        </div>
      )}

      {lastPoll && (
        <div className="mt-3 text-xs text-muted">
          Last poll: <span className={STATUS_TONE[lastPoll.status]}>{lastPoll.status}</span>{' '}
          {dateTime(lastPoll.createdAt)}
          {lastPoll.status === 'done' && (
            <span>
              {' '}
              — {integer(lastPoll.rowCount)} ASIN{lastPoll.rowCount === 1 ? '' : 's'},{' '}
              {integer(lastPoll.inserted)} snapshot{lastPoll.inserted === 1 ? '' : 's'},{' '}
              {integer(lastPoll.updated)} seller{lastPoll.updated === 1 ? '' : 's'}
              {lastPoll.skipped ? `, ${integer(lastPoll.skipped)} failed` : ''}
            </span>
          )}
          {lastPoll.status === 'failed' && !pricingRoleMissing && lastPoll.error && (
            <div className="mt-1 max-w-md break-words text-danger-text">{lastPoll.error}</div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * The product-catalog fetch: a "Fetch catalog now" button, the last run's outcome
 * (items stored, images stored, ASINs skipped as recently fetched), and — when
 * Amazon last refused for the Product Listing role — a notice pointing at the setup guide.
 * The nightly cycle fetches the catalog after the reports; this is the manual
 * trigger. Mirrors the offers section.
 */
function CatalogSection({
  catalog,
  onFetch,
  busy,
}: {
  catalog: SpapiStatus['catalog'];
  onFetch: () => void;
  busy: boolean;
}) {
  const { lastFetch, fetching, listingRoleMissing, productTypes } = catalog;
  return (
    <div className="rounded-md bg-sunken p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-body">Product catalog</h3>
          <p className="mt-1 text-xs text-muted">
            Titles and images from the Catalog Items API, for every child ASIN (Active first), shown
            as thumbnails across the app. Fetched nightly after the reports; an ASIN fetched in the
            last 30 days is skipped.
          </p>
        </div>
        <Button variant="secondary" disabled={busy || fetching} onClick={onFetch}>
          {fetching ? 'Fetching…' : 'Fetch catalog now'}
        </Button>
      </div>

      {listingRoleMissing && (
        <div className="mt-3">
          <Alert tone="warning">
            Product Listing role not granted. Amazon refused the Catalog Items API (403). Request
            the Product Listing role for the app —{' '}
            <a
              className="text-brand-600 underline"
              href={SETUP_GUIDE_URL}
              target="_blank"
              rel="noreferrer"
            >
              setup guide
            </a>{' '}
            (docs/sp-api-setup.md). The nightly cycle retries the next night.
          </Alert>
        </div>
      )}

      {lastFetch && (
        <div className="mt-3 text-xs text-muted">
          Last run: <span className={STATUS_TONE[lastFetch.status]}>{lastFetch.status}</span>{' '}
          {dateTime(lastFetch.createdAt)}
          {lastFetch.status === 'done' && (
            <span>
              {' '}
              — {integer(lastFetch.inserted)} item{lastFetch.inserted === 1 ? '' : 's'},{' '}
              {integer(lastFetch.updated)} image{lastFetch.updated === 1 ? '' : 's'} stored,{' '}
              {integer(lastFetch.skipped)} ASIN{lastFetch.skipped === 1 ? '' : 's'} skipped
            </span>
          )}
          {lastFetch.status === 'done' && lastFetch.error && (
            <div className="mt-1 max-w-md break-words text-muted">{lastFetch.error}</div>
          )}
          {lastFetch.status === 'failed' && !listingRoleMissing && lastFetch.error && (
            <div className="mt-1 max-w-md break-words text-danger-text">{lastFetch.error}</div>
          )}
        </div>
      )}

      {productTypes.length > 0 && <ProductTypeList productTypes={productTypes} />}
    </div>
  );
}

function InboundSection({
  inbound,
  onFetch,
  busy,
}: {
  inbound: SpapiStatus['inbound'];
  onFetch: () => void;
  busy: boolean;
}) {
  const { lastFetch, fetching, fulfillmentRoleMissing } = inbound;
  return (
    <div className="rounded-md bg-sunken p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-sm font-medium text-body">Inbound shipments</h3>
          <p className="mt-1 text-xs text-muted">
            FBA inbound shipments from the Fulfillment Inbound API, paired with plan sends and
            ledger receipts to measure the real supplier-to-check-in lead time. Fetched nightly; a
            rolling run also revisits every open shipment. See the observed lead times on the
            Purchase plan.
          </p>
        </div>
        <Button variant="secondary" disabled={busy || fetching} onClick={onFetch}>
          {fetching ? 'Fetching…' : 'Fetch inbound now'}
        </Button>
      </div>

      {fulfillmentRoleMissing && (
        <div className="mt-3">
          <Alert tone="warning">
            Amazon Fulfillment role not granted. Amazon refused the Fulfillment Inbound API (403).
            Request the Amazon Fulfillment role for the app —{' '}
            <a
              className="text-brand-600 underline"
              href={SETUP_GUIDE_URL}
              target="_blank"
              rel="noreferrer"
            >
              setup guide
            </a>{' '}
            (docs/sp-api-setup.md). The nightly cycle retries the next night.
          </Alert>
        </div>
      )}

      {lastFetch && (
        <div className="mt-3 text-xs text-muted">
          Last run: <span className={STATUS_TONE[lastFetch.status]}>{lastFetch.status}</span>{' '}
          {dateTime(lastFetch.createdAt)}
          {lastFetch.status === 'done' && (
            <span>
              {' '}
              — {integer(lastFetch.inserted)} new, {integer(lastFetch.updated)} updated,{' '}
              {integer(lastFetch.skipped)} skipped
            </span>
          )}
          {lastFetch.status === 'done' && lastFetch.error && (
            <div className="mt-1 max-w-md break-words text-muted">{lastFetch.error}</div>
          )}
          {lastFetch.status === 'failed' && !fulfillmentRoleMissing && lastFetch.error && (
            <div className="mt-1 max-w-md break-words text-danger-text">{lastFetch.error}</div>
          )}
        </div>
      )}
    </div>
  );
}

function RunsTable({
  runs,
  kindLabels,
}: {
  runs: SpapiFetchRun[];
  kindLabels: Record<ReportKind, string>;
}) {
  if (runs.length === 0) {
    return <p className="text-xs text-muted">No fetches yet.</p>;
  }
  return (
    <div>
      <h3 className="text-sm font-medium text-body">Recent fetches</h3>
      <TableScroll>
        <table className="mt-2 w-full text-sm" aria-label="SP-API recent fetches">
          <thead className="border-b border-line text-left text-xs text-muted">
            <tr>
              <th className="px-3 py-2 font-medium">Report</th>
              <th className="px-3 py-2 font-medium">Window</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">When</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {runs.map((run) => (
              <tr key={run.id}>
                <td className="px-3 py-2">
                  <div className="text-body">{fetchKindLabel(run.kind, kindLabels)}</div>
                  <div className="text-xs text-muted">
                    {run.mode} · {run.trigger}
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-muted">
                  {run.windowFrom ? `${day(run.windowFrom)} – ${day(run.windowTo)}` : 'full'}
                </td>
                <td className={`px-3 py-2 ${STATUS_TONE[run.status]}`}>
                  <div>{run.status}</div>
                  {run.error && (
                    <div
                      className={`max-w-xs text-xs break-words ${
                        run.status === 'failed' ? 'text-danger-text' : 'text-muted'
                      }`}
                    >
                      {run.error}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-muted">
                  {dateTime(run.createdAt)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
    </div>
  );
}

/** How many product types the catalog section shows before "Show all". */
const PRODUCT_TYPES_COLLAPSED = 8;

/**
 * Every product type the catalog fetch resolved, most common first, each marked
 * with how the taxonomy reads it: mapped (→ its category), generic (a catch-all
 * that says nothing), or unmapped — a listing issue, shown in the warning tone and
 * counted in the Products review queue. A long list collapses behind "Show all",
 * but the counts line above it always accounts for every type.
 */
function ProductTypeList({
  productTypes,
}: {
  productTypes: SpapiStatus['catalog']['productTypes'];
}) {
  const [expanded, setExpanded] = useState(false);
  const unmapped = productTypes.filter((t) => t.category === null && !t.generic);
  const generic = productTypes.filter((t) => t.generic).length;
  const mapped = productTypes.length - unmapped.length - generic;
  const shown = expanded ? productTypes : productTypes.slice(0, PRODUCT_TYPES_COLLAPSED);

  return (
    <div className="mt-2 text-xs text-muted" data-testid="catalog-product-types">
      <p>
        Product types: {integer(productTypes.length)} — {integer(mapped)} mapped, {integer(generic)}{' '}
        generic,{' '}
        <span className={unmapped.length > 0 ? 'font-medium text-warn-text' : ''}>
          {integer(unmapped.length)} unmapped
        </span>
        {unmapped.length > 0 && (
          <>
            {' '}
            (
            <Link to="/products/review?filter=listing-issues" className="text-brand-600 underline">
              listing issues
            </Link>
            )
          </>
        )}
      </p>
      <ul className="mt-1 flex flex-wrap gap-1">
        {shown.map((t) => {
          const isUnmapped = t.category === null && !t.generic;
          return (
            <li
              key={t.productType}
              className={`rounded px-1.5 py-0.5 ring-1 ring-inset ${
                isUnmapped
                  ? 'bg-warn-bg text-warn-text ring-warn-line'
                  : 'bg-surface text-body ring-line'
              }`}
              title={
                t.category
                  ? `Mapped to the ${t.category} category`
                  : t.generic
                    ? 'Generic: says nothing about the category, so the titles decide'
                    : 'Unmapped: each listing with this type is a listing issue to review'
              }
            >
              <span className="font-mono">{t.productType}</span> ({integer(t.count)}){' '}
              {t.category ? (
                <span className="text-muted">→ {t.category}</span>
              ) : t.generic ? (
                <span className="text-muted">generic</span>
              ) : (
                <span className="font-medium">unmapped</span>
              )}
            </li>
          );
        })}
      </ul>
      {productTypes.length > PRODUCT_TYPES_COLLAPSED && (
        <button
          type="button"
          className="mt-1 text-brand-600 hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Show fewer' : `Show all ${integer(productTypes.length)}`}
        </button>
      )}
    </div>
  );
}
